import { api } from './api';
import { ApiResponse } from './api';
import { DispatchFilters } from '../types';

export const dispatchService = {
  getDispatches: async (filters?: DispatchFilters): Promise<any> => {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.clientId) params.append('clientId', filters.clientId.toString());
    }
    
    const response = await api.get<ApiResponse<any>>(`/dispatch?${params}`);
    return response.data;
  },

  createByGateEntry: async (gateEntryId: number): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/by-gate-entry/${gateEntryId}`);
    // The axios interceptor already unwraps response.data, so 'response' is the API response object
    // API response structure: {success, statusCode, message, data: {dispatch object}, timestamp}
    // We need to return the dispatch object from response.data
    return response.data;
  },
  
  getDispatch: async (dispatchId: number): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(`/dispatch/${dispatchId}`);
    // The axios interceptor already unwraps response.data
    return response.data;
  },
  
  getDispatchItems: async (dispatchId: number): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(`/dispatch/${dispatchId}/items`);
    // The axios interceptor already unwraps response.data
    // response.data contains the array of items
    return response.data as unknown as any[];
  },
  
  addDispatchItem: async (dispatchId: number, payload: any): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/${dispatchId}/items`, payload);
    return response.data;
  },
  
  confirmDispatch: async (dispatchId: number, data?: { remarks?: string; destination?: string; lrNumber?: string; eWaybillNumber?: string; vehicleNo?: string; driverName?: string; driverPhone?: string; transporterName?: string; lrDate?: string; handlingType?: 'manual' | 'mhe' }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/${dispatchId}/confirm`, data);
    return response.data;
  },
  
  approveDispatch: async (dispatchId: number): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/${dispatchId}/approve`);
    return response.data;
  },
  
  securityApproveDispatch: async (dispatchId: number): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/${dispatchId}/security-approve`);
    return response.data;
  },
  
  rejectDispatch: async (dispatchId: number): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(`/dispatch/${dispatchId}/reject`);
    return response.data;
  },
  
  updateDispatchItem: async (dispatchId: number, itemId: number, payload: any): Promise<any> => {
    const response = await api.patch<ApiResponse<any>>(`/dispatch/${dispatchId}/items/${itemId}`, payload);
    return response.data;
  },

  deleteDispatchItem: async (dispatchId: number, itemId: number): Promise<void> => {
    await api.delete(`/dispatch/${dispatchId}/items/${itemId}`);
  },

  // ---- Past Dispatch update workflow (edit approved/completed dispatches) ----
  // The global ResponseInterceptor wraps every response as { data, ... }, so we
  // return `response.data` to get the actual payload.

  // Submit a request to edit an approved (completed) dispatch
  createPastDispatchUpdate: async (
    dispatchId: number,
    payload: PastDispatchUpdatePayload,
  ): Promise<any> => {
    const response = await api.post(`/dispatch/${dispatchId}/past-updates`, payload);
    return response.data;
  },

  // List past dispatch update requests (Admin/Super Admin only, enforced by backend)
  listPastDispatchUpdates: async (status: string = 'pending'): Promise<any> => {
    const response = await api.get('/dispatch/past-updates', { params: { status } });
    return response.data;
  },

  // Get a single past dispatch update request with diff detail
  getPastDispatchUpdate: async (updateId: number): Promise<any> => {
    const response = await api.get(`/dispatch/past-updates/${updateId}`);
    return response.data;
  },

  // Approve a past dispatch update (Admin/Super Admin only)
  approvePastDispatchUpdate: async (updateId: number): Promise<any> => {
    const response = await api.post(`/dispatch/past-updates/${updateId}/approve`);
    return response.data;
  },

  // Reject a past dispatch update (Admin/Super Admin only)
  rejectPastDispatchUpdate: async (
    updateId: number,
    rejectionReason: string,
  ): Promise<any> => {
    const response = await api.post(`/dispatch/past-updates/${updateId}/reject`, {
      rejectionReason,
    });
    return response.data;
  },

  // Reopen an approved (completed) dispatch for full re-editing on the Create
  // Dispatch screen. Reverses its inventory (adds stock back + restores pallets)
  // and sets it back to draft. Returns the dispatch so the caller can navigate.
  reopenPastDispatch: async (dispatchId: number): Promise<any> => {
    const response = await api.post(`/dispatch/${dispatchId}/reopen-for-edit`);
    return response.data;
  },
};

export interface PastDispatchItemEditPayload {
  dispatchItemId: number;
  goodQty?: number;
  cdamQty?: number;
  damQty?: number;
  palletCount?: number;
  batchNo?: string;
  remarks?: string;
}

export interface PastDispatchUpdatePayload {
  editReason: string;
  header?: {
    destination?: string;
    lrNumber?: string;
    eWaybillNumber?: string;
    vehicleNo?: string;
    driverName?: string;
    driverPhone?: string;
    transporterName?: string;
    lrDate?: string;
    handlingType?: 'manual' | 'mhe';
    remarks?: string;
  };
  items: PastDispatchItemEditPayload[];
}